/**
 * Botón "Sincronizar desde SET" reusable: dispara el endpoint
 * `/api/admin/tenants/:id/set-access/sync` (el mismo del MarangatuSnapshotCard)
 * que actualiza el snapshot completo del perfil + presentaciones históricas.
 * Lo usamos en los tabs Facturas y Declaraciones del admin para que cada vista
 * tenga su acción de sync visible sin tener que ir al tab SET.
 *
 * Tras el sync, invalida las queries pasadas en `invalidateKeys` para que el
 * listado del tab se refresque automáticamente.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

const ERROR_LABEL: Record<string, string> = {
  access_not_authorized: 'El titular no autorizó el acceso a su cuenta.',
  no_credentials: 'No hay credenciales de Marangatu cargadas.',
  login_failed: 'No pudimos loguear (¿credenciales mal o cambió SET?).',
  login_offsite: 'El login redirigió fuera de SET — abortado.',
  sync_failed: 'Falló el scrape. Probá de nuevo.',
};

export function SyncFromSetButton({
  tenantId,
  label = 'Sincronizar desde SET',
  size = 'sm',
  variant = 'outline',
  invalidateKeys = [],
}: {
  tenantId: string;
  label?: string;
  size?: 'sm' | 'default';
  variant?: 'outline' | 'default' | 'ghost';
  invalidateKeys?: unknown[][];
}) {
  const qc = useQueryClient();
  const sync = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; error?: string }>(`/api/admin/tenants/${tenantId}/set-access/sync`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success('Datos sincronizados desde Marangatu');
        // Invalidar queries explícitas del tab + ambos query keys de snapshot:
        // 'mar-snapshot' (panel admin) y 'app-mar-snapshot' (panel tenant) por si
        // el admin está mirando su propio tenant en otra pestaña.
        invalidateKeys.forEach((key) => qc.invalidateQueries({ queryKey: key }));
        qc.invalidateQueries({ queryKey: ['mar-snapshot', tenantId] });
        qc.invalidateQueries({ queryKey: ['app-mar-snapshot'] });
      } else {
        toast.error(ERROR_LABEL[r.error ?? ''] ?? 'No se pudo sincronizar');
      }
    },
    onError: () => toast.error('No se pudo sincronizar'),
  });

  return (
    <Button size={size} variant={variant} disabled={sync.isPending} onClick={() => sync.mutate()}>
      <RefreshCw className={sync.isPending ? 'animate-spin' : ''} />
      {sync.isPending ? 'Sincronizando…' : label}
    </Button>
  );
}
